/**
 * Tax-class transport, including the general-ledger mapping.
 *
 * A tax class says what KIND of thing is being sold, not what rate applies —
 * rates live in the finance tax codes, deliberately outside this contract. The
 * mapping is the join between the two worlds: which account a class's sales
 * tax and purchase tax land in.
 *
 * Three contract facts the screen depends on:
 *
 *   • The list is a LOOKUP — id and name only. The full record, with its code,
 *     description and status, comes from reading one class.
 *   • Every write is guarded by If-Match. A stale write is a 412, not a silent
 *     overwrite of somebody else's edit.
 *   • The mapping is replaced whole, and either side may be cleared with an
 *     explicit null. Deleting the mapping removes both at once, which is a
 *     different act from clearing one.
 */
import type { components } from "@erp/api-client";

import { apiFetch, apiRequest, type Page } from "@/lib/api/http";
import { fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type TaxClass = Schemas["CatalogTaxClass"];
export type TaxClassLookup = Schemas["CatalogTaxClassLookup"];
export type TaxClassCreate = Schemas["CatalogTaxClassCreate"];
export type TaxClassPatch = Schemas["CatalogTaxClassPatch"];
export type TaxClassGlMapping = Schemas["CatalogTaxClassGlMapping"];
export type TaxClassGlMappingPut = Schemas["CatalogTaxClassGlMappingPut"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

export function listTaxClasses(
  query: { status?: string } = {},
  signal?: AbortSignal,
): Promise<Page<TaxClassLookup>> {
  return apiFetch<Page<TaxClassLookup>>("/tax-classes", {
    query: { ...query, limit: 200 },
    signal,
  });
}

export function getTaxClass(taxClassId: string): Promise<WithEtag<TaxClass>> {
  return fetchWithEtag<TaxClass>(`/tax-classes/${encodeURIComponent(taxClassId)}`);
}

export function createTaxClass(input: TaxClassCreate, idempotencyKey: string): Promise<TaxClass> {
  return apiFetch<TaxClass>("/tax-classes", { method: "POST", body: input, idempotencyKey });
}

export async function updateTaxClass(
  taxClassId: string,
  patch: TaxClassPatch,
  ifMatch: string,
): Promise<WithEtag<TaxClass>> {
  const { data, etag } = await apiRequest<TaxClass>(
    `/tax-classes/${encodeURIComponent(taxClassId)}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

export function deleteTaxClass(taxClassId: string, ifMatch: string): Promise<void> {
  return apiFetch<void>(`/tax-classes/${encodeURIComponent(taxClassId)}`, {
    method: "DELETE",
    ifMatch,
  });
}

// ── The general-ledger mapping ───────────────────────────────────────────

export function getTaxClassGlMapping(taxClassId: string): Promise<WithEtag<TaxClassGlMapping>> {
  return fetchWithEtag<TaxClassGlMapping>(
    `/tax-classes/${encodeURIComponent(taxClassId)}/gl-mapping`,
  );
}

/**
 * Replaces the mapping whole. An explicit null clears one side; omitting a
 * side leaves it as it was.
 *
 * The precondition is the TAX CLASS's ETag, not the mapping's — mapping and
 * class share one version, so writing a mapping bumps the class and a class
 * edit held from before the mapping write is stale. Creating the first mapping
 * still requires it (428 otherwise), which is why the caller passes the
 * class's tag rather than whatever a missing mapping returned.
 */
export async function putTaxClassGlMapping(
  taxClassId: string,
  body: TaxClassGlMappingPut,
  taxClassEtag: string,
): Promise<WithEtag<TaxClassGlMapping>> {
  const { data, etag } = await apiRequest<TaxClassGlMapping>(
    `/tax-classes/${encodeURIComponent(taxClassId)}/gl-mapping`,
    { method: "PUT", body, ifMatch: taxClassEtag },
  );
  return { data, etag };
}

export function deleteTaxClassGlMapping(taxClassId: string): Promise<void> {
  return apiFetch<void>(`/tax-classes/${encodeURIComponent(taxClassId)}/gl-mapping`, {
    method: "DELETE",
  });
}
